<template>
    <div>
        <div v-if="withdraw.userBankAccount" class="my-3">
            <b-alert variant="dark" show>
                <h5 class="mb-0">
                    <font-awesome-icon
                        icon="fa-duotone fa-triangle-exclamation"
                    />
                    {{ $t("Confirm Bank Details") }}
                </h5>

                <div class="mt-3">
                    <div class="small text-muted">
                        {{ $t("Account Name") }}
                    </div>
                    <code class="lead d-block">{{
                        withdraw.userBankAccount.name
                    }}</code>
                </div>

                <div class="mt-3">
                    <div class="small text-muted">
                        {{ $t("Routing Number") }}
                    </div>
                    <code class="lead d-block">
                        {{ withdraw.userBankAccount.routing }}
                    </code>
                </div>

                <div class="mt-3">
                    <div class="small text-muted">
                        {{ $t("Account Number") }}
                    </div>
                    <code class="lead d-block">
                        {{ withdraw.userBankAccount.account }}
                    </code>
                </div>

                <div class="mt-3">
                    <div class="small text-muted">
                        {{ $t("Account Type") }}
                    </div>
                    <code class="lead d-block">
                        {{ withdraw.userBankAccount.type }}
                    </code>
                </div>

                <div class="small mt-2">
                    {{
                        $t(
                            "If you need to make a correction, press cancel to start over"
                        )
                    }}
                </div>

                <div class="small mt-2 text-warning">
                    <font-awesome-icon
                        icon="fa-duotone fa-triangle-exclamation"
                    />
                    {{
                        $t(
                            "ACH transfers can take 1–3 business days. Make sure your numbers are correct to avoid delays."
                        )
                    }}
                </div>
            </b-alert>

            <b-btn
                @click="submit"
                :disabled="submitting"
                variant="primary"
                size="lg"
                class="mt-3"
                pill
                block
            >
                {{ $t("Submit Withdrawal") }}
            </b-btn>

            <b-btn
                @click="cancelWithdraw"
                :disabled="submitting"
                variant="light"
                size="lg"
                class="mt-2"
                pill
                block
            >
                {{ $t("Cancel") }}
            </b-btn>
        </div>
        <div v-else>
            <div v-if="userBankAccounts?.length" class="border">
                <h5 class="text-center p-3">
                    {{ $t("Existing Bank Accounts") }}
                </h5>
                <b-table-simple striped class="mb-0">
                    <tbody>
                        <tr
                            :key="userBankAccount.id"
                            v-for="userBankAccount in userBankAccounts"
                        >
                            <td>
                                {{ userBankAccount.type }}
                            </td>
                            <td>
                                {{ userBankAccount.name }}
                            </td>
                            <td class="text-right">
                                <b-btn
                                    @click="useAccount(userBankAccount)"
                                    variant="primary"
                                    size="sm"
                                    pill
                                >
                                    {{ $t("Select") }}
                                </b-btn>
                            </td>
                        </tr>
                    </tbody>
                </b-table-simple>
            </div>
            <b-alert v-else variant="info" show class="small">
                <font-awesome-icon icon="fa-duotone fa-info-circle" />
                ACH payments are temporarily being paid out manually. Your
                patience is appreciated.
                <div class="mt-3">
                    USDC payments are instant and fully automated.
                </div>
            </b-alert>

            <div v-if="creating" class="mt-3">
                <b-alert variant="danger" show v-if="error">
                    {{ error }}
                </b-alert>

                <b-form-group :label="$t('Account Holder\'s legal name')">
                    <b-form-input
                        v-model="name"
                        tabindex="1"
                        class="border-light"
                        size="lg"
                        autocomplete="off"
                        :placeholder="$t('Account Name')"
                    />

                    <small v-if="!name" class="text-warning">
                        {{
                            $t(
                                "Enter your full legal name exactly as it appears on your account"
                            )
                        }}
                    </small>
                </b-form-group>

                <b-form-group :label="$t('Routing Number')">
                    <b-form-input
                        v-model="routing"
                        tabindex="2"
                        class="border-light"
                        size="lg"
                        autocomplete="off"
                        :placeholder="$t('Routing number')"
                    />
                </b-form-group>

                <b-form-group :label="$t('Account Number')">
                    <b-form-input
                        v-model="account"
                        tabindex="3"
                        class="border-light"
                        size="lg"
                        autocomplete="off"
                        :placeholder="$t('Your bank account number')"
                    />
                </b-form-group>

                <b-form-group :label="$t('Account Type')">
                    <b-select
                        size="lg"
                        v-model="type"
                        :options="types"
                        autocomplete="off"
                        class="border-light"
                    >
                    </b-select>
                </b-form-group>

                <b-btn
                    @click="save"
                    variant="primary"
                    size="lg"
                    pill
                    block
                    class="mt-2"
                >
                    {{ $t("Save Bank Details") }}
                </b-btn>
            </div>
            <div v-else>
                <b-btn
                    @click="creating = true"
                    variant="primary"
                    pill
                    block
                    class="mt-3"
                >
                    {{ $t("Add New Account") }}
                </b-btn>
            </div>

            <b-button v-b-toggle.achHelp variant="light" pill class="mt-3">
                <font-awesome-icon icon="fa-duotone fa-info-circle" size="lg" />
                {{ $t("I Need Help") }}
            </b-button>
            <b-collapse id="achHelp">
                <div class="mt-3">
                    <div>
                        {{
                            $t(
                                "Find your routing and account numbers on a check or in your bank app/statement."
                            )
                        }}
                    </div>
                    <div class="mt-2">
                        {{
                            $t(
                                "Routing number is 9 digits. Account number length varies by bank."
                            )
                        }}
                    </div>
                    <div class="mt-3">
                        <code class="d-block">{{ $t("Example:") }}</code>
                        <code class="d-block">Name: Jane Doe</code>
                        <code class="d-block">Routing: 123456789</code>
                        <code class="d-block">Account: 000123456789</code>
                    </div>
                </div>

                <b-alert variant="secondary" show class="mt-3">
                    <h3 class="mt-3">
                        {{ $t("Outside of the United States") }}
                    </h3>
                    <div>
                        <p>
                            <strong>ACH transfers</strong> are the standard way
                            of sending money to US Dollar bank accounts. Even if
                            you’re outside the US, you can still get your own
                            USD account through apps like <em>Revolut</em> or
                            <em>Wise</em>.
                        </p>

                        <p>Here’s how to set it up in Revolut:</p>
                        <ul>
                            <li>
                                Open the Revolut app and create a
                                <strong>USD wallet</strong>.
                            </li>
                            <li>
                                Tap <strong>“View details”</strong> on your new
                                USD wallet.
                            </li>
                            <li>
                                Copy the
                                <strong>ACH account details</strong> (account +
                                routing number) shown there — that’s what you’ll
                                use to receive transfers.
                            </li>
                        </ul>

                        <p>
                            Once you’ve got those details, you can share them
                            with anyone sending you money in USD via ACH.
                        </p>
                    </div>
                </b-alert>
            </b-collapse>
        </div>
    </div>
</template>

<script>
import { mapState } from "vuex";

export default {
    data() {
        return {
            creating: false,
            error: null,
            name: null,
            routing: null,
            account: null,
            type: null,
            types: ["Checking", "Savings", "Business Checking"],
            userBankAccounts: null,
        };
    },
    mounted() {
        this.refresh();
    },
    computed: {
        ...mapState("withdraw", ["withdraw", "lastUserPayee", "submitting"]),
        ...mapState({
            onboardingUser: (state) => state.onboarding.user,
        }),
    },
    methods: {
        useAccount(userBankAccount) {
            this.error = null;

            this.api
                .post("/withdraw/bankAccount", {
                    withdrawId: this.withdraw.id,
                    userBankAccountId: userBankAccount.id,
                })
                .then((response) => {
                    if (response?.data?.withdraw) {
                        this.$store.dispatch(
                            "withdraw/setWithdraw",
                            response.data.withdraw
                        );
                    }
                })
                .catch((error) => {
                    if (error?.response?.data?.message) {
                        this.error = error.response.data.message;
                    } else {
                        this.error = this.$t(
                            "Something went wrong. Please try again."
                        );
                    }
                });
        },
        cancelWithdraw() {
            this.$store.dispatch("withdraw/cancel");
        },
        refresh() {
            this.api.post("/bankAccount/index", {}).then((response) => {
                this.userBankAccounts = response?.data?.userBankAccounts;

                // default for new
                if (!this.userBankAccounts?.length) {
                    this.creating = true;
                }
            });
        },
        save() {
            this.error = null;

            this.api
                .post("/withdraw/ach", {
                    withdrawId: this.withdraw.id,
                    name: this.name,
                    routing: this.routing,
                    account: this.account,
                    type: this.type,
                })
                .then((response) => {
                    if (response?.data?.withdraw) {
                        this.$store.dispatch(
                            "withdraw/setWithdraw",
                            response.data.withdraw
                        );
                    }
                })
                .catch((error) => {
                    if (error?.response?.data?.message) {
                        this.error = error.response.data.message;
                    } else {
                        this.error = this.$t(
                            "Something went wrong. Please try again."
                        );
                    }
                });
        },
        submit() {
            this.$store.dispatch("withdraw/submit");
        },
    },
    components: {},
};
</script>
